import React from "react";
import { Gauge, Database, HardDrive, Mail, ShieldCheck } from "lucide-react";
import { Chip, SectionHeader, Loading, LoadError, usePlatformFetch } from "./_primitives";

function Row({ label, val }) {
  if (!val) return null;
  const pct = val.success_rate_pct;
  const state = pct >= 99 ? "green" : pct >= 95 ? "amber" : pct >= 1 ? "red" : "missing";
  return (
    <div className="flex items-center gap-3 text-xs py-2 border-t border-[var(--fop-border)]" data-testid={`reliability-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-28 font-mono uppercase tracking-wider text-[10px] text-[var(--fop-text-2)]">{label}</div>
      <div className="flex-1 h-1.5 bg-[var(--fop-surface)] rounded-full overflow-hidden">
        <div className={`h-full ${pct >= 99 ? "bg-emerald-400" : pct >= 95 ? "bg-amber-400" : pct > 0 ? "bg-red-400" : "bg-zinc-700"}`} style={{ width: `${Math.max(val.total ? 4 : 0, pct)}%` }} />
      </div>
      <div className="w-16 text-right font-mono text-[11px] text-[var(--fop-text-2)]">{val.total ? `${pct}%` : "—"}</div>
      <div className="w-24 text-right font-mono text-[10px] text-[var(--fop-text-4)]">{val.succeeded}/{val.total} ok</div>
      <Chip state={state} label={val.total ? state : "no events"} />
    </div>
  );
}

export default function ReliabilityCenter() {
  const { data, error, retry } = usePlatformFetch("/platform/reliability");
  if (error) return <LoadError error={error} retry={retry} label="Reliability" />;
  if (!data) return <Loading />;
  const i = data.infra || {};
  const s = data.surfaces_24h || {};
  return (
    <div className="space-y-4" data-testid="reliability-center">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="fop-surface-2 rounded-lg p-3 border border-[var(--fop-border)]" data-testid="rel-db">
          <div className="inline-flex items-center gap-1.5 text-[var(--fop-text-2)]"><Database size={11} /><span className="text-[10px] font-mono uppercase tracking-wider">Database</span></div>
          <Chip state={i.database?.ok ? "ok" : "down"} className="mt-2" label={i.database?.ok ? "OK" : "DOWN"} />
          <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-1">{i.database?.latency_ms ?? "—"} ms</div>
        </div>
        <div className="fop-surface-2 rounded-lg p-3 border border-[var(--fop-border)]" data-testid="rel-storage">
          <div className="inline-flex items-center gap-1.5 text-[var(--fop-text-2)]"><HardDrive size={11} /><span className="text-[10px] font-mono uppercase tracking-wider">Storage</span></div>
          <Chip state={i.storage?.configured ? "ok" : (i.storage?.backend === "local" ? "local" : "misconfigured")} className="mt-2" label={i.storage?.backend === "local" ? "LOCAL · DEV" : (i.storage?.backend || "—").toUpperCase()} />
          <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-1">{i.storage?.backend === "local" ? "Ephemeral disk — set R2_* for prod" : "Cloud storage"}</div>
        </div>
        <div className="fop-surface-2 rounded-lg p-3 border border-[var(--fop-border)]" data-testid="rel-email">
          <div className="inline-flex items-center gap-1.5 text-[var(--fop-text-2)]"><Mail size={11} /><span className="text-[10px] font-mono uppercase tracking-wider">Email</span></div>
          <Chip state={i.email?.configured ? "ok" : "missing"} className="mt-2" label={i.email?.configured ? "OK" : "Missing"} />
          <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-1">RESEND_API_KEY</div>
        </div>
        <div className="fop-surface-2 rounded-lg p-3 border border-[var(--fop-border)]" data-testid="rel-webhook">
          <div className="inline-flex items-center gap-1.5 text-[var(--fop-text-2)]"><ShieldCheck size={11} /><span className="text-[10px] font-mono uppercase tracking-wider">Webhook</span></div>
          <Chip state={i.webhook?.configured ? "ok" : "missing"} className="mt-2" label={i.webhook?.configured ? "OK" : "Missing"} />
          <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-1">INBOUND_WEBHOOK_SECRET</div>
        </div>
      </div>

      <div className="fop-surface-2 rounded-lg p-4" data-testid="reliability-surfaces">
        <SectionHeader icon={<Gauge size={11} />} title="Success rates · last 24 hours" />
        <Row label="Auth login" val={s.auth_login} />
        <Row label="Uploads" val={s.uploads} />
        <Row label="OCR" val={s.ocr} />
        <Row label="Email" val={s.email} />
        <Row label="Intake" val={s.intake} />
        <Row label="Share" val={s.share} />
        <Row label="Export" val={s.export} />
      </div>

      <div className="fop-surface-2 rounded-lg p-4" data-testid="reliability-fleet">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-2">Fleet error rate · 7d</div>
        <div className="flex items-baseline gap-2">
          <div className="font-display text-3xl">{data.fleet_error_rate_7d_pct}%</div>
          <div className="text-[10px] font-mono text-[var(--fop-text-4)]">{data.fleet_failed_7d} / {data.fleet_events_7d} audit events flagged</div>
        </div>
      </div>
    </div>
  );
}
